import { useIsAuthenticated } from "@azure/msal-react";
import { useEffect, useMemo, useState } from "react";
import {
  getResidenceTypeFilters,
  ResidenceTypeFilterModel,
} from "../../clients/KompasApi";
import { Flex, Spinner, Text } from "@chakra-ui/react";
import { CustomerFilter, customerIdAtom } from "./CustomerFilter";
import { RoofTypeFilter, roofTypeIdsAtom } from "./RoofTypeFilter";
import {
  ResidenceCategoryFilter,
  residenceCategoryIdsAtom,
} from "./ResidenceCategoryFilter";
import { useAtom } from "jotai";
import { ResetFiltersButton } from "./ResetFiltersButton";

interface Props {
  search: string;
}

export function ResidenceTypeFilters({ search }: Props) {
  const isAuthenticated = useIsAuthenticated();
  const [currentFilters, setCurrentFilters] =
    useState<ResidenceTypeFilterModel>();
  const [loading, setLoading] = useState(false);

  const [customerId] = useAtom(customerIdAtom);
  const [roofTypeIds] = useAtom(roofTypeIdsAtom);
  const [residenceCategoryIds] = useAtom(residenceCategoryIdsAtom);

  useEffect(() => {
    if (!isAuthenticated) return;

    const abortController = new AbortController();

    setLoading(true);

    getResidenceTypeFilters({
      query: {
        ResidenceCategoryIds: residenceCategoryIds,
        RoofTypeIds: roofTypeIds,
        CustomerId: customerId,
        Search: search || undefined,
      },
      signal: abortController.signal,
      throwOnError: true,
    })
      .then(({ data }) => {
        if (abortController.signal.aborted) return;
        setCurrentFilters(data);
      })
      .catch((e) => {
        if (abortController.signal.aborted) return;
        throw e;
      })
      .finally(() => setLoading(false));

    return () => abortController.abort("Component unmounted or re-rendered");
  }, [isAuthenticated, residenceCategoryIds, roofTypeIds, customerId, search]);

  /* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
  const anyFilterAvailable = useMemo(
    () =>
      currentFilters?.residenceCategories?.length ||
      currentFilters?.roofTypes?.length ||
      currentFilters?.customers?.length,
    [currentFilters],
  );
  /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */

  return (
    <Flex direction="column" flex={1} gap={4}>
      <Flex alignItems="baseline" fontWeight="500" gap={4}>
        <Text fontSize="md">Filters</Text>
        <ResetFiltersButton />
      </Flex>
      <CustomerFilter options={currentFilters?.customers ?? []} />
      <ResidenceCategoryFilter
        options={currentFilters?.residenceCategories ?? []}
        value={
          currentFilters?.residenceCategories.filter((rc) => rc.selected) ?? []
        }
      />
      <RoofTypeFilter options={currentFilters?.roofTypes ?? []} />
      {!anyFilterAvailable &&
        (loading ? (
          <Spinner />
        ) : (
          <Text color="gray.500" fontSize="sm">
            Geen filters beschikbaar
          </Text>
        ))}
    </Flex>
  );
}
